'use client';

import { useState, useTransition } from 'react';
import { track } from '@vercel/analytics';
import { ArrowRight } from 'lucide-react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { Alert, AlertDescription } from '@kit/ui/alert';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { Input } from '@kit/ui/input';
import { Textarea } from '@kit/ui/textarea';

import type { MarketingLocale } from '~/lib/marketing/site';
import { CONTACT_EMAIL } from '~/lib/marketing/site';

import { CONTACT_SECTION } from '../_lib/home.content';
import { MarketingContactSchema } from '../_lib/marketing-contact.schema';
import { sendMarketingContactEmail } from '../_lib/server/marketing-contact-actions';
import { SchedulingModal } from './google-calendar-button';

/**
 * Landing-page reach-out section (#contact): a lead form that emails the
 * sales inbox, next to a "Book an appointment" button opening the Google
 * Calendar scheduling modal.
 */
export function ContactSection({ locale }: { locale: MarketingLocale }) {
  const c = CONTACT_SECTION;
  const [pending, startTransition] = useTransition();
  const [schedulingOpen, setSchedulingOpen] = useState(false);
  const [state, setState] = useState<'idle' | 'success' | 'error'>('idle');

  const form = useForm({
    resolver: zodResolver(MarketingContactSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      venue: '',
      message: '',
      website: '' as const,
    },
  });

  return (
    <section
      id="contact"
      className="bg-[#f7f6f4] py-10 md:py-16 lg:py-24 px-4 md:px-8 lg:px-[130px]"
    >
      <div className="max-w-[1255px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
        {/* Intro + scheduling */}
        <div>
          <div className="flex items-center gap-3 mb-4 lg:mb-6">
            <div className="w-2 h-2 bg-[#202020] rounded-full" aria-hidden="true" />
            <p className="font-roboto-mono uppercase text-[12px] md:text-[14px] text-[#202020] tracking-wide">
              {c.label[locale]}
            </p>
          </div>
          <h2
            className="text-[28px] md:text-[34px] lg:text-[40px] font-roboto font-normal leading-[1.2] mb-4 text-[#202020]"
            style={{ fontVariationSettings: "'wdth' 100" }}
          >
            {c.heading[locale]}
          </h2>
          <p className="text-[16px] md:text-[18px] font-roboto text-[#202020] opacity-70 leading-[1.5] mb-8 max-w-[480px]">
            {c.description[locale]}
          </p>

          <button
            onClick={() => setSchedulingOpen(true)}
            className="bg-[#202020] text-white h-[50px] md:h-[56px] px-6 md:px-8 inline-flex items-center gap-3 hover:bg-[#2b2b2b] transition-colors group cursor-pointer"
          >
            <span className="font-roboto-mono uppercase text-[12px] md:text-[14px] font-bold">
              {c.bookAppointment[locale]}
            </span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform rtl:rotate-180 rtl:group-hover:-translate-x-1" />
          </button>

          <p className="mt-8 font-roboto-mono text-[13px] text-[#8a857c]">
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="underline-offset-4 hover:underline"
            >
              {CONTACT_EMAIL}
            </a>
          </p>
        </div>

        {/* Lead form */}
        <div className="bg-white border border-[#dcd7cf] rounded-[16px] p-6 md:p-8">
          {state === 'success' ? (
            <Alert variant="success">
              <AlertDescription>{c.form.success[locale]}</AlertDescription>
            </Alert>
          ) : (
            <Form {...form}>
              <form
                className="flex flex-col gap-4"
                onSubmit={form.handleSubmit((data) => {
                  startTransition(async () => {
                    try {
                      await sendMarketingContactEmail(data);
                      track('marketing_contact_submitted');
                      setState('success');
                    } catch {
                      track('marketing_contact_failed');
                      setState('error');
                    }
                  });
                })}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{c.form.name[locale]}</FormLabel>
                        <FormControl>
                          <Input maxLength={200} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{c.form.email[locale]}</FormLabel>
                        <FormControl>
                          <Input type="email" maxLength={320} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{c.form.phone[locale]}</FormLabel>
                        <FormControl>
                          <Input maxLength={30} dir="ltr" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    name="venue"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{c.form.venue[locale]}</FormLabel>
                        <FormControl>
                          <Input maxLength={200} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{c.form.message[locale]}</FormLabel>
                      <FormControl>
                        <Textarea className="min-h-32" maxLength={5000} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Honeypot: hidden from real users, catches naive bots */}
                <FormField
                  name="website"
                  render={({ field }) => (
                    <div className="hidden" aria-hidden="true">
                      <Input tabIndex={-1} autoComplete="off" {...field} />
                    </div>
                  )}
                />

                {state === 'error' && (
                  <Alert variant="destructive">
                    <AlertDescription>{c.form.error[locale]}</AlertDescription>
                  </Alert>
                )}

                <button
                  type="submit"
                  disabled={pending}
                  className="bg-[#202020] text-white h-[50px] px-8 inline-flex items-center justify-center gap-3 hover:bg-[#2b2b2b] transition-colors disabled:opacity-60 cursor-pointer"
                >
                  <span className="font-roboto-mono uppercase text-[12px] font-bold">
                    {c.form.submit[locale]}
                  </span>
                </button>
              </form>
            </Form>
          )}
        </div>
      </div>

      <SchedulingModal open={schedulingOpen} onClose={() => setSchedulingOpen(false)} />
    </section>
  );
}
